import React from "react";
import { Activity, HeartPulse, Compass, AlertTriangle, CircleDot } from "lucide-react";

/**
 * Concept-level overview panel for the Performance Lab section.
 *
 * Replaces the previous live-style dashboard so the landing page doesn't make
 * implicit product promises (no live indicator, no session timer, no specific
 * metric values, no Break Protocol or Focus Signal logic). Shows three
 * monitoring categories at a category level and a small flagged-students
 * roster with traffic-light status indicators only.
 */
export default function HealthDashboardMock() {
  return (
    <div
      className="glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden"
      data-testid="health-dashboard-mock"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-mono-tech uppercase tracking-widest text-[#00FFD0]">
            Performance Lab · Overview
          </p>
          <p className="mt-1 text-[10px] font-mono-tech uppercase tracking-widest text-[#52525b]">
            Concept Interface
          </p>
        </div>
        <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[#52525b]">
          For Illustration
        </span>
      </div>

      {/* Three monitoring categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dashboard-categories">
        <CategoryTile icon={Activity} title="Performance" tone="orange" status="On Track" />
        <CategoryTile icon={HeartPulse} title="Wellness" tone="mint" status="On Track" />
        <CategoryTile icon={Compass} title="Pathways" tone="orange" status="Tracking" />
      </div>

      {/* Student roster — traffic-light status only, no numeric values */}
      <div className="mt-6 glass rounded-xl p-5" data-testid="student-roster">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-mono-tech uppercase tracking-widest text-[#A1A1AA]">
            Cohort Snapshot
          </p>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-tech uppercase tracking-widest text-[#FFB05E]">
            <AlertTriangle className="w-3 h-3" /> 2 Flags
          </span>
        </div>
        <ul className="divide-y divide-white/5">
          {[
            { id: "Student · 01", session: "Active", status: "green", flags: 0 },
            { id: "Student · 02", session: "On Break", status: "green", flags: 0 },
            { id: "Student · 03", session: "Active", status: "amber", flags: 1 },
            { id: "Student · 04", session: "Completed", status: "green", flags: 0 },
            { id: "Student · 05", session: "Active", status: "red", flags: 1 },
          ].map((s) => (
            <li key={s.id} className="py-2.5 flex items-center justify-between text-sm">
              <span className="text-[#D4D4D8] font-mono-tech text-xs">{s.id}</span>
              <div className="flex items-center gap-5">
                <span className="text-[11px] font-mono-tech uppercase tracking-widest text-[#6a6a70]">
                  {s.session}
                </span>
                <span className="inline-flex items-center gap-2 min-w-[78px] justify-end">
                  <StatusDot tone={s.status} />
                  <span className="text-[11px] font-mono-tech uppercase tracking-widest text-[#A1A1AA]">
                    {s.status === "green" ? "OK" : s.status === "amber" ? "Watch" : "Flag"}
                  </span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function CategoryTile({ icon: Icon, title, tone, status }) {
  const color = tone === "mint" ? "#00FFD0" : "#FF8C00";
  return (
    <div className="glass rounded-xl p-5">
      <div className="flex items-center justify-between">
        <Icon className="w-5 h-5" style={{ color }} />
        <StatusDot tone="green" />
      </div>
      <p className="mt-4 font-display font-black text-lg text-white">{title}</p>
      <p className="text-[11px] uppercase tracking-widest text-[#A1A1AA] mt-1">{status}</p>
    </div>
  );
}

function StatusDot({ tone }) {
  const color = tone === "amber" ? "#FFB05E" : tone === "red" ? "#FF5A4A" : "#00FFD0";
  return (
    <CircleDot
      className="w-3 h-3"
      style={{ color, filter: `drop-shadow(0 0 6px ${color})` }}
    />
  );
}
